import useSWR, {SWRConfiguration} from "swr";
import {defaultSWRConfig, swrStaticConfig} from "../swr-config";
import {apiEndpoints} from "@/utils/api-endpoints";
import {fetcher} from "@/lib/swr-config";
import {getQueryString} from "@/lib/url/query-string";
import {ProductsParamsType} from "@/lib/products";
import {Product, ProductGroup, ProductPicture} from "@/types/product";

interface ApiResponse<T> {
    status: string;
    data: T;
}

export function useProductLastUpdate(options?: SWRConfiguration<string>) {
    const swr = useSWR<string>(apiEndpoints.Product.GetUpdateDateTime.url, fetcher, {
        ...defaultSWRConfig,
        ...options,
    });

    return {
        data: swr.data,
        error: swr.error,
        isLoading: !swr.data && !swr.error,
        isValidating: swr.isValidating,
        mutate: swr.mutate,
    };
}

export function useProductList<TData>(
    productParams: ProductsParamsType,
    initialData?: TData,
    options?: SWRConfiguration<TData>
) {
    const fullParams = { ...productParams };
    const queryString = getQueryString(fullParams);
    const url = apiEndpoints.Product.GetProducts.url(queryString);

    const swr = useSWR<TData>(url, fetcher, {
        fallbackData: initialData,
        ...defaultSWRConfig,
        ...options,
    });

    return {
        data: swr.data,
        error: swr.error,
        isLoading: swr.isLoading && !(initialData && productParams.Page === 1),
        isValidating: swr.isValidating,
        mutate: swr.mutate,
    };
}

export function useSliderProducts(
    offer: boolean,
    options?: SWRConfiguration<{ data: any[] }>
) {
    const url = offer
        ? apiEndpoints.Product.GetOffers10.url
        : apiEndpoints.Product.GetNewests10.url;

    const swr = useSWR<{ data: any[] }>(url, fetcher, {
        ...swrStaticConfig,
        ...options,
    });

    return {
        data: swr.data,
        error: swr.error,
        isLoading: !swr.data && !swr.error,
        isValidating: swr.isValidating,
        mutate: swr.mutate,
    };
}

export function useProductGroups(options?: SWRConfiguration<ProductGroup[]>) {
    const {url} = apiEndpoints.Product.ProductGroup.Groups;

    const swr = useSWR<ProductGroup[]>(url, fetcher, {
        ...swrStaticConfig,
        ...options,
    });

    return {
        data: swr.data,
        error: swr.error,
        isLoading: !swr.data && !swr.error,
        isValidating: swr.isValidating,
        mutate: swr.mutate,
    };
}

export function useProductDetails(
    productId: string | undefined,
    options?: SWRConfiguration<Product>
) {
    const shouldFetch = !!productId;

    const url = shouldFetch
        ? apiEndpoints.Product.GetDetails.url(productId)
        : null;

    const swr = useSWR<Product>(url, fetcher, {
        ...defaultSWRConfig,
        ...options,
    });

    return {
        data: swr.data,
        error: swr.error,
        isLoading: shouldFetch && !swr.data && !swr.error,
        isValidating: swr.isValidating,
        mutate: swr.mutate,
    };
}

export function useProductImages(
    productId: string,
    options?: SWRConfiguration<ApiResponse<ProductPicture[]>>
) {
    const url = apiEndpoints.Product.Images.GetImages.url(productId);

    const swr = useSWR<ApiResponse<ProductPicture[]>>(url, fetcher, {
        ...defaultSWRConfig,
        ...options,
    });

    return {
        data: swr.data,
        error: swr.error,
        isLoading: !swr.data && !swr.error,
        isValidating: swr.isValidating,
        mutate: swr.mutate,
    };
}

export function useProductPriceChart(
    productId: number,
    options?: SWRConfiguration<ApiResponse<ProductPicture[]>>
) {
    const url = apiEndpoints.Product.GetProductPriceChart.url(productId.toString());

    const swr = useSWR<ApiResponse<ProductPicture[]>>(url, fetcher, {
        ...defaultSWRConfig,
        ...options,
    });

    return {
        data: swr.data,
        error: swr.error,
        isLoading: !swr.data && !swr.error,
        isValidating: swr.isValidating,
        mutate: swr.mutate,
    };
}